import { useEffect, useState, useRef, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { getBlock } from '@bst/blocks'
import { useEditorStore } from './store/editor.store'
import { BlockRenderer } from './components/blocks/renderers'
import { Canvas } from './components/Canvas'
import { BlockLibrary } from './components/Sidebar/BlockLibrary'
import { LayersPanel } from './components/Sidebar/LayersPanel'
import { PagesPanel } from './components/Pages'
import { PropertiesPanel } from './components/Properties'
import { VisualInspector } from './components/Inspector/VisualInspector'
import { SettingsModal } from './components/Settings'
import { SubmissionsModal } from './components/SubmissionsModal'
import { CmsPanel } from './components/CmsPanel'
import { MediaLibraryModal } from './components/MediaLibraryModal'
import { ShopPanel } from './components/ShopPanel'
import { PeerCursors } from './components/PeerCursors'
import { AnalyticsPanel } from './components/AnalyticsPanel'
import { SeoAuditPanel } from './components/SeoAuditPanel'
import { AiGenerateModal } from './components/AiGenerateModal'
import { VersionHistory } from './components/VersionHistory'
import { ThemePickerModal } from './components/ThemePicker'
import { ShareModal } from './components/ShareModal'
import { OnboardingTour } from './components/OnboardingTour'
import { BulkSeoEditor } from './components/BulkSeoEditor'
import { KeyboardShortcutsModal } from './components/KeyboardShortcutsModal'
import { AiChatPanel } from './components/AiChatPanel'
import { GlobalSearchModal } from './components/GlobalSearchModal'
import { NewsletterPanel } from './components/NewsletterPanel'
import { ResponsivePreviewModal } from './components/ResponsivePreviewModal'
import { JobProgressToast } from './components/JobProgressToast'
import { SiteUsersModal } from './components/SiteUsersModal'
import { StyleSwitcherModal } from './components/StyleSwitcherModal'
import { CodeViewModal } from './components/CodeViewModal'
import { ClientHandoffModal } from './components/ClientHandoffModal'
import { exportToHTML } from './utils/htmlExport'
import { useAutoSave } from './hooks/useAutoSave'
import { useCollab } from './hooks/useCollab'
import { usePageLazyLoad } from './hooks/usePageLazyLoad'
import { sitesApi, projectsApi, api, workspacesApi, trackEvent, designSystemApi } from './utils/api'
import { useState as _useState, useEffect as _useEffect } from 'react'

function RightSidebarWithInspector() {
  const [mode, setMode] = _useState<'properties' | 'inspector'>('properties')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <button
          onClick={() => setMode('properties')}
          style={{
            flex: 1, padding: '5px 4px', fontSize: 11, fontWeight: mode === 'properties' ? 700 : 400,
            border: 'none', borderBottom: mode === 'properties' ? '2px solid var(--accent)' : '2px solid transparent',
            background: 'none', cursor: 'pointer',
            color: mode === 'properties' ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >Свойства</button>
        <button
          onClick={() => setMode('inspector')}
          style={{
            flex: 1, padding: '5px 4px', fontSize: 11, fontWeight: mode === 'inspector' ? 700 : 400,
            border: 'none', borderBottom: mode === 'inspector' ? '2px solid var(--accent)' : '2px solid transparent',
            background: 'none', cursor: 'pointer',
            color: mode === 'inspector' ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >Стили</button>
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {mode === 'properties' ? <PropertiesPanel /> : <VisualInspector />}
      </div>
    </div>
  )
}

export default function App() {
  const navigate = useNavigate()
  const {
    sidebarPanel, setSidebarPanel,
    device, setDevice,
    page, pages,
    settings,
    zoom, setZoom,
    showGrid, toggleGrid,
    settingsOpen, setSettingsOpen,
    isReadOnly,
    loadedPageIds,
  } = useEditorStore()
  const canUndo = useEditorStore((s) => s.canUndo())
  const canRedo = useEditorStore((s) => s.canRedo())
  const setUserPlan = useEditorStore((s) => s.setUserPlan)
  const [dark, setDark] = useState(() => localStorage.getItem('bst-theme') === 'dark')
  const [publishOpen, setPublishOpen] = useState(false)
  const [published, setPublished] = useState(false)
  const [siteIsPublished, setSiteIsPublished] = useState(false)
  const [copied, setCopied] = useState(false)
  const [submissionsOpen, setSubmissionsOpen] = useState(false)
  const [cmsOpen, setCmsOpen] = useState(false)
  const [mediaOpen, setMediaOpen] = useState(false)
  const [shopOpen, setShopOpen] = useState(false)
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [newsletterOpen, setNewsletterOpen] = useState(false)
  const [seoOpen, setSeoOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [themeOpen, setThemeOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [commentsEnabled, setCommentsEnabled] = useState(false)
  const [bulkSeoOpen, setBulkSeoOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifCounts, setNotifCounts] = useState<{ submissions: number; orders: number } | null>(null)
  const [aiChatOpen, setAiChatOpen] = useState(false)
  const [siteUsersOpen, setSiteUsersOpen] = useState(false)
  const [styleOpen, setStyleOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [whitelabel, setWhitelabel] = useState<{ name?: string; logo?: string; color?: string } | null>(null)
  const [responsivePreviewOpen, setResponsivePreviewOpen] = useState(false)
  const [codeViewOpen, setCodeViewOpen] = useState(false)
  const [clientHandoffOpen, setClientHandoffOpen] = useState(false)
  const activeJobs = useEditorStore((s) => s.activeJobs)
  const removeJob = useEditorStore((s) => s.removeJob)
  const [firstPublishOpen, setFirstPublishOpen] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [mobilePanel, setMobilePanel] = useState<'blocks' | 'layers' | 'pages' | 'properties' | null>(null)
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false)
  const [navMoreOpen, setNavMoreOpen] = useState(false)
  const navMoreRef = useRef<HTMLDivElement>(null)
  // Swipe-to-dismiss bottom sheet
  const sheetRef = useRef<HTMLDivElement>(null)
  const sheetSwipeStartY = useRef(0)
  const sheetSwipeDelta = useRef(0)
  const publishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const publishingRef = useRef(false)
  const blockClipboard = useRef<import('./store/editor.store').BlockState | null>(null)
  const { siteId } = useAutoSave()
  usePageLazyLoad()

  // ── Design System tokens — load when projectId is set ────────────────────────
  const projectId = useEditorStore(s => s.projectId)
  const setDesignTokens = useEditorStore(s => s.setDesignTokens)
  _useEffect(() => {
    if (!projectId) return
    designSystemApi.get(projectId).then((ds) => setDesignTokens(ds.tokens)).catch(() => undefined)
  }, [projectId, setDesignTokens])

  // ── DnD ─────────────────────────────────────────────────────────────────────
  const [draggingBlockId, setDraggingBlockId] = useState<string | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: isReadOnly ? 99999 : 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  )

  function handleDragStart(event: DragStartEvent) {
    const data = event.active.data.current
    if (data?.type === 'block') setDraggingBlockId(data.blockId as string)
    // On mobile: close the panel so canvas is visible while dragging
    if (mobilePanel) setMobilePanel(null)
  }

  function handleDragEnd(event: DragEndEvent) {
    setDraggingBlockId(null)
    const { active, over } = event
    if (!over) return
    const activeData = active.data.current
    const overData = over.data.current
    const { page: p, addBlock, moveBlock } = useEditorStore.getState()

    if (activeData?.type === 'new-block') {
      const blockType = activeData.blockType as string
      const def = getBlock(blockType)
      if (!def) return
      let targetSectionId: string | null = null
      if (overData?.type === 'section') targetSectionId = overData.sectionId as string
      else if (overData?.type === 'block') targetSectionId = overData.sectionId as string
      const sec = targetSectionId
        ? p.sections.find((s) => s.id === targetSectionId)
        : p.sections[p.sections.length - 1]
      if (sec) {
        const data = (activeData.savedBlockData ?? activeData.customBlockData) as Record<string, unknown> | undefined
        addBlock(sec.id, blockType, data ?? def.defaultData)
      }
      return
    }

    if (activeData?.type === 'block') {
      const fromSection = activeData.sectionId as string
      const blockId = activeData.blockId as string
      let toSection = fromSection
      let toIndex = 0
      if (overData?.type === 'section') {
        toSection = overData.sectionId as string
        const sec = p.sections.find((s) => s.id === toSection)
        toIndex = sec ? sec.blocks.length : 0
      } else if (overData?.type === 'block') {
        toSection = overData.sectionId as string
        const sec = p.sections.find((s) => s.id === toSection)
        if (sec) {
          const overIdx = sec.blocks.findIndex((b) => b.id === over.id)
          toIndex = overIdx >= 0 ? overIdx : sec.blocks.length
        }
      }
      if (fromSection === toSection) {
        const sec = p.sections.find((s) => s.id === fromSection)
        if (!sec) return
        const oldIdx = sec.blocks.findIndex((b) => b.id === blockId)
        const newIdx = sec.blocks.findIndex((b) => b.id === over.id)
        if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return
        moveBlock(fromSection, blockId, toSection, newIdx)
      } else {
        moveBlock(fromSection, blockId, toSection, toIndex)
      }
    }
  }

  const draggingBlock = draggingBlockId
    ? page.sections.flatMap((s) => s.blocks).find((b) => b.id === draggingBlockId)
    : null
  // ─────────────────────────────────────────────────────────────────────────────
  const saveStatus = useEditorStore((s) => s.saveStatus)
  const saveError = useEditorStore((s) => s.saveError)
  const { peers, broadcastCursor, broadcastSelection } = useCollab(siteId)

  // Broadcast selection changes to peers
  const selectedBlockId = useEditorStore((s) => s.selectedBlockId)
  useEffect(() => {
    broadcastSelection(selectedBlockId)
  }, [selectedBlockId, broadcastSelection])

  useEffect(() => {
    if (!navMoreOpen) return
    function onOutside(e: MouseEvent) {
      if (navMoreRef.current && !navMoreRef.current.contains(e.target as Node)) setNavMoreOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [navMoreOpen])

  // Track mouse position over canvas for cursor broadcasting
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    broadcastCursor(e.clientX, e.clientY)
  }, [broadcastCursor])

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('bst-theme', dark ? 'dark' : 'light')
  }, [dark])

  const handleThemeToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2
    document.documentElement.style.setProperty('--bst-tx', `${x}px`)
    document.documentElement.style.setProperty('--bst-ty', `${y}px`)
    if (!document.startViewTransition) { setDark(d => !d); return }
    document.startViewTransition(() => { flushSync(() => setDark(d => !d)) })
  }

  // Auto-open AI modal when site was created via "Сайт с помощью AI" flow
  useEffect(() => {
    if (localStorage.getItem('bst-open-ai') === '1') {
      localStorage.removeItem('bst-open-ai')
      setAiOpen(true)
    }
  }, [])

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    if (isMobile && selectedBlockId) setMobilePanel('properties')
  }, [selectedBlockId, isMobile])

  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      if (saveStatus === 'dirty' || saveStatus === 'saving' || saveStatus === 'error') {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [saveStatus])

  function logout() {
    localStorage.removeItem('bst-auth')
    localStorage.removeItem('bst-token')
    navigate('/', { replace: true })
  }

  function downloadHTML() {
    const html = exportToHTML(page, settings, siteId ?? undefined)
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${page.slug?.replace(/\//g, '') || 'site'}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Load real published status on mount
  useEffect(() => {
    const id = localStorage.getItem('bst-site-id')
    if (!id) return
    sitesApi.get(id).then(site => {
      if (site?.status === 'PUBLISHED') setSiteIsPublished(true)
    }).catch(() => null)
    // Load user plan
    sitesApi.getPlanUsage().then(usage => {
      setUserPlan(usage.plan)
      if (usage.plan === 'HOSTING') {
        navigate('/dashboard', { replace: true })
      }
    }).catch(() => null)
    // Load workspace white-label branding
    workspacesApi.list().then(memberships => {
      const ws = memberships[0]?.workspace
      if (ws?.whitelabelEnabled) {
        setWhitelabel({ name: ws.whitelabelName, logo: ws.whitelabelLogo, color: ws.whitelabelColor })
      }
    }).catch(() => null)
    // Fetch notification counts (submissions + orders)
    Promise.all([
      api.get<{ data: unknown[] }>(`/sites/${id}/submissions`).then(r => (Array.isArray(r) ? r : (r as { data: unknown[] }).data ?? r).length).catch(() => 0),
      api.get<unknown[]>(`/sites/${id}/orders`).then(r => (Array.isArray(r) ? r : []).length).catch(() => 0),
    ]).then(([submissions, orders]) => setNotifCounts({ submissions: submissions as number, orders: orders as number }))
  }, [])

  async function handlePublish() {
    // Block publish if there are unsaved changes — user must wait for autosave to complete
    if (saveStatus === 'dirty' || saveStatus === 'saving') return
    if (publishingRef.current) return

    const projectId = localStorage.getItem('bst-project-id')
    const currentSiteId = siteId ?? localStorage.getItem('bst-site-id')
    if (!projectId && !currentSiteId) return

    const wasPublished = siteIsPublished
    publishingRef.current = true
    try {
      if (projectId) {
        await projectsApi.publish(projectId)
      } else {
        await sitesApi.publish(currentSiteId!)
      }
      setPublishError(null)
      setSiteIsPublished(true)
      setPublished(true)
      if (publishTimerRef.current) clearTimeout(publishTimerRef.current)
      publishTimerRef.current = setTimeout(() => setPublished(false), 3000)
      // Show first-publish celebration only once
      if (!wasPublished) {
        setPublishOpen(false)
        setFirstPublishOpen(true)
      }
    } catch (e) {
      setPublishError(e instanceof Error && e.message ? e.message : 'Ошибка публикации. Проверьте соединение и попробуйте ещё раз.')
    } finally {
      publishingRef.current = false
    }
  }

  function copyUrl(url: string) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const target = e.target as HTMLElement
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return

      const store = useEditorStore.getState()
      if (store.isReadOnly) return

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(v => !v)
        return
      }

      if (e.key === 'Escape') {
        store.selectBlock(null)
        store.selectSection(null)
        return
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (store.selectedBlockId) {
          const { page, selectedBlockId, globalHeader, globalFooter } = store
          if (globalHeader?.blocks.find(b => b.id === selectedBlockId)) {
            store.removeBlock('__global_header__', selectedBlockId)
          } else if (globalFooter?.blocks.find(b => b.id === selectedBlockId)) {
            store.removeBlock('__global_footer__', selectedBlockId)
          } else {
            for (const sec of page.sections) {
              if (sec.blocks.find((b) => b.id === selectedBlockId)) {
                store.removeBlock(sec.id, selectedBlockId)
                break
              }
            }
          }
        } else if (store.selectedSectionId) {
          store.removeSection(store.selectedSectionId)
        }
        return
      }

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault()
        store.undo()
        return
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault()
        store.redo()
        return
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault()
        store.setZoom(store.zoom + 10)
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault()
        store.setZoom(store.zoom - 10)
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault()
        store.setZoom(100)
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "'") {
        e.preventDefault()
        store.toggleGrid()
        return
      }

      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLSelectElement)) {
        setShortcutsOpen(v => !v)
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault()
        if (store.selectedBlockId) {
          const { page, selectedBlockId, globalHeader, globalFooter } = store
          if (globalHeader?.blocks.find(b => b.id === selectedBlockId)) {
            store.duplicateBlock('__global_header__', selectedBlockId)
          } else if (globalFooter?.blocks.find(b => b.id === selectedBlockId)) {
            store.duplicateBlock('__global_footer__', selectedBlockId)
          } else {
            for (const sec of page.sections) {
              if (sec.blocks.find((b) => b.id === selectedBlockId)) {
                store.duplicateBlock(sec.id, selectedBlockId)
                break
              }
            }
          }
        } else if (store.selectedSectionId) {
          store.duplicateSection(store.selectedSectionId)
        }
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault()
        const dir = e.key === 'ArrowUp' ? -1 : 1
        if (store.selectedBlockId) {
          const { page, selectedBlockId } = store
          for (const sec of page.sections) {
            const idx = sec.blocks.findIndex(b => b.id === selectedBlockId)
            if (idx >= 0) {
              const newIdx = idx + dir
              if (newIdx >= 0 && newIdx < sec.blocks.length) {
                store.moveBlock(sec.id, selectedBlockId, sec.id, newIdx)
              }
              break
            }
          }
        } else if (store.selectedSectionId) {
          if (dir === -1) store.moveSectionUp(store.selectedSectionId)
          else store.moveSectionDown(store.selectedSectionId)
        }
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && store.selectedBlockId) {
        e.preventDefault()
        const { page, selectedBlockId, globalHeader, globalFooter } = store
        const allBlocks = [
          ...(globalHeader?.blocks ?? []),
          ...(globalFooter?.blocks ?? []),
          ...page.sections.flatMap(s => s.blocks),
        ]
        const block = allBlocks.find(b => b.id === selectedBlockId)
        if (block) blockClipboard.current = block
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && blockClipboard.current) {
        e.preventDefault()
        const clip = blockClipboard.current
        const { selectedSectionId, page } = store
        const targetSectionId = selectedSectionId ?? page.sections[page.sections.length - 1]?.id
        if (targetSectionId) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id: _id, ...rest } = clip
          store.pasteBlock(targetSectionId, rest)
        }
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const totalBlocks = page.sections.reduce((s, sec) => s + sec.blocks.length, 0)

  return (
    <div className="editor-layout" onMouseMove={handleMouseMove}>
      {/* ── Mobile: Compact Header ─────────────────────────────────────────────── */}
      {isMobile && (
        <header className="mobile-editor-header">
          <button
            className="mobile-header-back"
            onClick={() => navigate('/dashboard')}
            title="Мои сайты"
          >←</button>
          <div className="mobile-header-center">
            <span className="mobile-header-title">
              {settings.name || 'Без названия'}
            </span>
            {saveStatus === 'dirty' && <span className="mobile-save-dot" style={{ background: '#94a3b8' }} title="Есть изменения…" />}
            {saveStatus === 'saving' && <span className="mobile-save-dot" style={{ background: '#f59e0b' }} title="Сохранение…" />}
            {saveStatus === 'error' && <span className="mobile-save-dot" style={{ background: '#ef4444' }} title={saveError ?? 'Ошибка сохранения'} />}
            {(saveStatus === 'idle' || saveStatus === 'saved') && <span className="mobile-save-dot" style={{ background: '#22c55e' }} title="Сохранено" />}
          </div>
          <div className="mobile-header-actions">
            <button className="mobile-header-btn" disabled={!canUndo} onClick={() => useEditorStore.getState().undo()} title="Отменить" aria-label="Отменить">↩</button>
            <button className="mobile-header-btn" disabled={!canRedo} onClick={() => useEditorStore.getState().redo()} title="Повторить" aria-label="Повторить">↪</button>
            <button className="mobile-header-btn" onClick={() => { setMobileMoreOpen(v => !v); setMobilePanel(null) }} title="Ещё" aria-label="Больше действий">⋯</button>
            {!isReadOnly && (
              <button className="mobile-header-publish" onClick={() => setPublishOpen(true)}>🚀</button>
            )}
            {isReadOnly && (
              <span className="mobile-readonly-badge">👁</span>
            )}
          </div>
        </header>
      )}

      {/* ── Desktop: Full Header ───────────────────────────────────────────────── */}
      {!isMobile && <header className="editor-header">
        <button
          className="editor-logo"
          onClick={() => navigate('/dashboard')}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          title="Мои сайты"
        >
          {whitelabel ? (
            <>
              {whitelabel.logo
                ? <img src={whitelabel.logo} alt={whitelabel.name ?? 'Logo'} style={{ height: '24px', objectFit: 'contain', maxWidth: '80px' }} />
                : <span className="logo-text" style={whitelabel.color ? { color: whitelabel.color } : undefined}>{whitelabel.name ?? 'BST'}</span>
              }
            </>
          ) : (
            <>
              <span className="logo-text">BST</span>
              <span className="logo-badge">beta</span>
            </>
          )}
        </button>
        <nav className="editor-nav">
          <button
            className={`nav-item ${sidebarPanel === 'blocks' || sidebarPanel === 'layers' ? 'active' : ''}`}
            onClick={() => setSidebarPanel('blocks')}
          >
            Редактор
          </button>
          <button
            data-tour="pages-tab"
            className={`nav-item ${sidebarPanel === 'pages' ? 'active' : ''}`}
            onClick={() => setSidebarPanel('pages')}
          >
            Страницы
            {pages.length > 1 && (
              <span style={{ marginLeft: '5px', background: 'var(--accent)', color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>
                {pages.length}
              </span>
            )}
          </button>
          <button
            className={`nav-item ${submissionsOpen ? 'active' : ''}`}
            onClick={() => setSubmissionsOpen(true)}
          >
            Заявки
          </button>
          <button
            className={`nav-item ${mediaOpen ? 'active' : ''}`}
            onClick={() => setMediaOpen(true)}
          >
            Медиа
          </button>
          <button
            data-tour="settings-btn"
            className={`nav-item ${settingsOpen ? 'active' : ''}`}
            onClick={() => setSettingsOpen(true)}
          >
            Настройки
          </button>
          {/* "Ещё" dropdown for secondary items */}
          <div ref={navMoreRef} style={{ position: 'relative' }}>
            <button
              className={`nav-item ${navMoreOpen || styleOpen || cmsOpen || shopOpen || analyticsOpen || newsletterOpen || siteUsersOpen || seoOpen || bulkSeoOpen || clientHandoffOpen ? 'active' : ''}`}
              onClick={() => setNavMoreOpen(o => !o)}
            >
              Ещё ▾
            </button>
            {navMoreOpen && (
              <div
                onClick={() => setNavMoreOpen(false)}
                style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                  background: 'var(--bg-1)', border: '1px solid var(--border)',
                  borderRadius: '10px', padding: '4px', zIndex: 500,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '150px',
                }}
              >
                {([
                  ['Стили', () => setStyleOpen(true), styleOpen],
                  ['CMS', () => setCmsOpen(true), cmsOpen],
                  ['Магазин', () => setShopOpen(true), shopOpen],
                  ['Аналитика', () => setAnalyticsOpen(true), analyticsOpen],
                  ['Рассылка', () => setNewsletterOpen(true), newsletterOpen],
                  ['Пользователи', () => setSiteUsersOpen(true), siteUsersOpen],
                  ['SEO', () => setSeoOpen(true), seoOpen],
                  ['SEO таблица', () => setBulkSeoOpen(true), bulkSeoOpen],
                  ['Client Handoff', () => setClientHandoffOpen(true), clientHandoffOpen],
                ] as [string, () => void, boolean][]).map(([label, action, active]) => (
                  <button
                    key={label}
                    onClick={action}
                    style={{
                      display: 'block', width: '100%', padding: '7px 12px',
                      background: active ? 'var(--bg-3)' : 'none',
                      border: 'none', borderRadius: '7px',
                      fontSize: '13px', color: 'var(--text-primary)',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = active ? 'var(--bg-3)' : 'none')}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>
        <div className="editor-actions">
          {saveStatus === 'dirty' && (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#94a3b8', display: 'inline-block' }} />
              Изменения…
            </span>
          )}
          {saveStatus === 'saving' && (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b', display: 'inline-block', animation: 'pulse 1s infinite' }} />
              Сохранение…
            </span>
          )}
          {saveStatus === 'error' && (
            <span title={saveError ?? undefined} style={{ fontSize: '11px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>
              ⚠ Ошибка сохранения
            </span>
          )}
          <button
            className="btn-ghost btn-sm"
            onClick={() => setSearchOpen(true)}
            title="Глобальный поиск (Ctrl+K)"
            style={{ fontSize: '13px', padding: '4px 10px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            🔍
            <kbd style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>⌘K</kbd>
          </button>
          <div className="history-btns">
            <button className="btn-ghost btn-sm" disabled={!canUndo} onClick={() => useEditorStore.getState().undo()} title="Отменить (Ctrl+Z)">↩</button>
            <button className="btn-ghost btn-sm" disabled={!canRedo} onClick={() => useEditorStore.getState().redo()} title="Повторить (Ctrl+Y)">↪</button>
            <button className="btn-ghost btn-sm" onClick={() => setHistoryOpen(true)} title="История версий" style={{ fontSize: '14px' }}>🕐</button>
            <button className="btn-ghost btn-sm" onClick={() => setShortcutsOpen(v => !v)} title="Горячие клавиши (?)" style={{ fontSize: '12px', fontWeight: 700, padding: '3px 7px' }}>?</button>
            <button
              className={`btn-ghost btn-sm${commentsEnabled ? ' active' : ''}`}
              onClick={() => setCommentsEnabled(v => !v)}
              title={commentsEnabled ? 'Выключить комментарии' : 'Режим комментариев'}
              style={{ fontSize: '14px', padding: '4px 7px', opacity: commentsEnabled ? 1 : 0.6 }}
            >💬</button>
            <div style={{ position: 'relative' }}>
              <button
                className="btn-ghost btn-sm"
                onClick={() => setNotifOpen(v => !v)}
                title="Уведомления"
                style={{ fontSize: '15px', padding: '4px 7px', position: 'relative' }}
              >
                🔔
                {notifCounts && (notifCounts.submissions + notifCounts.orders) > 0 && (
                  <span style={{ position: 'absolute', top: '1px', right: '1px', minWidth: '14px', height: '14px', background: '#ef4444', color: '#fff', borderRadius: '7px', fontSize: '9px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', lineHeight: 1 }}>
                    {notifCounts.submissions + notifCounts.orders}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', width: '220px', zIndex: 1000, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>Уведомления</div>
                  <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button onClick={() => { setSubmissionsOpen(true); setNotifOpen(false) }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-2)', border: 'none', borderRadius: '7px', padding: '8px 10px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>
                      <span>📬 Заявки с форм</span>
                      <span style={{ background: notifCounts && notifCounts.submissions > 0 ? '#ef4444' : '#e5e7eb', color: notifCounts && notifCounts.submissions > 0 ? '#fff' : '#888', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>{notifCounts?.submissions ?? '…'}</span>
                    </button>
                    <button onClick={() => { setShopOpen(true); setNotifOpen(false) }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-2)', border: 'none', borderRadius: '7px', padding: '8px 10px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>
                      <span>🛒 Заказы</span>
                      <span style={{ background: notifCounts && notifCounts.orders > 0 ? '#f59e0b' : '#e5e7eb', color: notifCounts && notifCounts.orders > 0 ? '#fff' : '#888', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>{notifCounts?.orders ?? '…'}</span>
                    </button>
                  </div>
                  <button onClick={() => setNotifOpen(false)} style={{ position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none', fontSize: '14px', color: '#999', cursor: 'pointer' }}>×</button>
                </div>
              )}
            </div>
          </div>
          <div className="device-switcher">
            <button className={`device-btn ${device === 'desktop' ? 'active' : ''}`} title="Компьютер" onClick={() => setDevice('desktop')}>🖥</button>
            <button className={`device-btn ${device === 'tablet' ? 'active' : ''}`} title="Планшет" onClick={() => setDevice('tablet')}>📱</button>
            <button className={`device-btn ${device === 'mobile' ? 'active' : ''}`} title="Телефон" onClick={() => setDevice('mobile')}>📲</button>
            <button className="device-btn" title="Все размеры экрана" onClick={() => { setResponsivePreviewOpen(true); trackEvent('editor.responsive_preview_opened') }}>📐</button>
            <button className="device-btn" title="Код сайта" onClick={() => { setCodeViewOpen(true); trackEvent('editor.code_view_opened') }} style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700 }}>{'</>'}</button>
          </div>
          <button
            className={`btn-ghost btn-sm${showGrid ? ' active' : ''}`}
            onClick={toggleGrid}
            title="Сетка (Ctrl+')"
            style={{ fontSize: '14px', padding: '4px 7px', opacity: showGrid ? 1 : 0.6 }}
          >#</button>
          <div className="zoom-control">
            <button className="btn-ghost btn-sm" onClick={() => setZoom(zoom - 10)} title="Уменьшить (Ctrl+-)">−</button>
            <span className="zoom-value" onClick={() => setZoom(100)} title="Сбросить (Ctrl+0)">{zoom}%</span>
            <button className="btn-ghost btn-sm" onClick={() => setZoom(zoom + 10)} title="Увеличить (Ctrl+=)">+</button>
          </div>
          <button
            className="btn-ghost btn-sm"
            onClick={handleThemeToggle}
            title={dark ? 'Светлая тема' : 'Тёмная тема'}
            style={{ fontSize: '16px', padding: '4px 8px' }}
          >{dark ? '☀' : '🌙'}</button>
          <button
            onClick={() => setThemeOpen(true)}
            className="btn-ghost btn-sm"
            title="Тема сайта"
            style={{ fontSize: '16px', padding: '4px 8px' }}
          >🎨</button>
          {!isReadOnly && (
            <button
              onClick={() => setAiOpen(true)}
              style={{ padding: '6px 14px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              title="AI: сгенерировать сайт из описания"
            >✨ AI</button>
          )}
          <button className="btn-ghost" onClick={() => window.open('/preview', '_blank')}>Preview</button>
          {!isReadOnly && <button className="btn-ghost" onClick={() => setShareOpen(true)} title="Совместный доступ" style={{ fontSize: '14px' }}>👥 Доступ</button>}
          {isReadOnly ? (
            <span style={{ padding: '6px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#6b7280', display: 'flex', alignItems: 'center', gap: '5px' }}>
              👁 Только просмотр
            </span>
          ) : (
            <button
              className="btn-primary"
              title={(saveStatus === 'dirty' || saveStatus === 'saving') ? 'Дождитесь сохранения…' : 'Опубликовать сайт'}
              style={(saveStatus === 'dirty' || saveStatus === 'saving') ? { opacity: 0.6 } : undefined}
              onClick={() => {
                if (saveStatus === 'dirty' || saveStatus === 'saving') return
                const hasContent = pages.some(p =>
                  loadedPageIds.includes(p.id) && p.sections.some(s => s.blocks.length > 0)
                )
                if (!hasContent) {
                  if (!confirm('Сайт не содержит блоков. Опубликовать пустую страницу?')) return
                }
                setPublishOpen(true)
              }}
            >Опубликовать</button>
          )}
          {peers.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              {peers.slice(0, 5).map(peer => (
                <div
                  key={peer.clientId}
                  title={peer.userName}
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    background: peer.color,
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--bg-1)',
                    marginLeft: '-6px',
                    cursor: 'default',
                    flexShrink: 0,
                  }}
                >
                  {peer.userName.charAt(0).toUpperCase()}
                </div>
              ))}
              {peers.length > 5 && (
                <div style={{ marginLeft: '4px', fontSize: '11px', color: 'var(--text-muted)' }}>
                  +{peers.length - 5}
                </div>
              )}
            </div>
          )}
          <button
            className="btn-ghost btn-sm"
            title="Выйти из аккаунта"
            onClick={logout}
            style={{ color: '#555', fontSize: '16px', padding: '4px 8px' }}
          >⏏</button>
        </div>
      </header>}

      {publishOpen && (
        <PublishModal
          page={page}
          siteId={siteId}
          siteSlug={localStorage.getItem('bst-site-slug') ?? (settings.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'my-site')}
          totalPages={pages.length}
          totalBlocks={totalBlocks}
          published={published}
          siteIsPublished={siteIsPublished}
          copied={copied}
          error={publishError}
          onPublish={handlePublish}
          onDownload={downloadHTML}
          onCopy={copyUrl}
          onClose={() => { setPublishOpen(false); setPublishError(null) }}
        />
      )}

      <SettingsModal />
      {submissionsOpen && <SubmissionsModal siteId={siteId} onClose={() => setSubmissionsOpen(false)} />}
      {cmsOpen && <CmsPanel siteId={siteId} onClose={() => setCmsOpen(false)} />}
      {mediaOpen && <MediaLibraryModal onClose={() => setMediaOpen(false)} />}
      {shopOpen && <ShopPanel siteId={siteId} onClose={() => setShopOpen(false)} />}
      {analyticsOpen && <AnalyticsPanel siteId={siteId} onClose={() => setAnalyticsOpen(false)} />}
      {newsletterOpen && <NewsletterPanel siteId={siteId} onClose={() => setNewsletterOpen(false)} />}
      {siteUsersOpen && <SiteUsersModal siteId={siteId} onClose={() => setSiteUsersOpen(false)} />}
      {styleOpen && <StyleSwitcherModal siteId={siteId} siteName={settings.name} onClose={() => setStyleOpen(false)} />}
      {seoOpen && <SeoAuditPanel siteId={siteId} onClose={() => setSeoOpen(false)} />}
      {bulkSeoOpen && <BulkSeoEditor onClose={() => setBulkSeoOpen(false)} />}
      {shortcutsOpen && <KeyboardShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      {searchOpen && <GlobalSearchModal onClose={() => setSearchOpen(false)} siteId={siteId ?? undefined} />}
      {aiOpen && <AiGenerateModal onClose={() => setAiOpen(false)} />}
      {historyOpen && <VersionHistory siteId={siteId} onClose={() => setHistoryOpen(false)} />}
      {themeOpen && <ThemePickerModal onClose={() => setThemeOpen(false)} />}
      {shareOpen && siteId && <ShareModal siteId={siteId} siteName={settings.name} onClose={() => setShareOpen(false)} />}
      {responsivePreviewOpen && (
        <ResponsivePreviewModal
          url="/preview"
          siteName={settings.name}
          onClose={() => setResponsivePreviewOpen(false)}
        />
      )}
      {codeViewOpen && <CodeViewModal onClose={() => setCodeViewOpen(false)} />}
      {clientHandoffOpen && siteId && (
        <ClientHandoffModal
          siteId={siteId}
          siteName={settings.name}
          siteSlug={localStorage.getItem('bst-site-slug') ?? ''}
          onClose={() => setClientHandoffOpen(false)}
        />
      )}
      <PeerCursors />
      {!isReadOnly && <OnboardingTour />}

      {/* §88 Job progress toasts */}
      {activeJobs.map((job, idx) => (
        <div key={job.jobId} style={{ position: 'fixed', bottom: 24 + idx * 90, right: 24, zIndex: 10001 }}>
          <JobProgressToast
            jobId={job.jobId}
            label={job.label}
            onCompleted={() => setTimeout(() => removeJob(job.jobId), 3500)}
            onFailed={() => setTimeout(() => removeJob(job.jobId), 6000)}
            onDismiss={() => removeJob(job.jobId)}
          />
        </div>
      ))}

      {/* AI Chat floating button — hidden on mobile (AI is in tab bar) */}
      {!isMobile && (
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9998, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {settings.aiApprovalLevel === 'auto' && (
            <div style={{ background: '#22c55e', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
              AUTO §76
            </div>
          )}
          <button
            onClick={() => { setAiChatOpen(v => { if (!v) trackEvent('editor.ai_chat_opened'); return !v }); }}
            title={settings.aiApprovalLevel === 'auto' ? 'AI Помощник (Автономный режим §76)' : 'AI Помощник'}
            style={{
              width: '52px', height: '52px',
              borderRadius: '50%', background: 'linear-gradient(135deg,#5b6af0,#7c3aed)',
              border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(91,106,240,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✨</button>
        </div>
      )}
      {aiChatOpen && <AiChatPanel onClose={() => setAiChatOpen(false)} />}

      {/* ── First-publish celebration ─────────────────────────────────────────── */}
      {firstPublishOpen && (
        <FirstPublishModal
          siteSlug={localStorage.getItem('bst-site-slug') ?? (settings.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'my-site')}
          siteId={siteId}
          onClose={() => setFirstPublishOpen(false)}
        />
      )}

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <main className="editor-main">
          {/* Left sidebar — desktop only */}
          {!isMobile && (
            <aside className="sidebar-left" data-tour="block-library">
              {sidebarPanel === 'pages' ? (
                <PagesPanel />
              ) : (
                <>
                  <div className="sidebar-tabs">
                    <button
                      className={`sidebar-tab ${sidebarPanel === 'blocks' ? 'active' : ''}`}
                      onClick={() => setSidebarPanel('blocks')}
                    >Блоки</button>
                    <button
                      className={`sidebar-tab ${sidebarPanel === 'layers' ? 'active' : ''}`}
                      onClick={() => setSidebarPanel('layers')}
                    >Слои</button>
                  </div>
                  {sidebarPanel === 'blocks' ? <BlockLibrary /> : <LayersPanel />}
                </>
              )}
            </aside>
          )}

          <div className="editor-canvas-wrapper">
            {!isMobile && (
              <div className="canvas-toolbar">
                <span className="canvas-page-name">{page.title}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                  {page.slug}
                </span>
                <span className="canvas-shortcuts">Del — удалить · Ctrl+D — дублировать · Ctrl+C/V — копировать/вставить · Ctrl+↑↓ — перемещение · Ctrl+Z — отмена</span>
              </div>
            )}
            <Canvas commentsEnabled={commentsEnabled && !isReadOnly} siteId={siteId} />
          </div>

          {/* Right sidebar — desktop only */}
          {!isMobile && (
            <aside className="sidebar-right" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <RightSidebarWithInspector />
            </aside>
          )}
        </main>

        <DragOverlay>
          {draggingBlock && (
            <div style={{ opacity: 0.85, pointerEvents: 'none', background: 'var(--bg-1)', borderRadius: '6px', overflow: 'hidden' }}>
              <BlockRenderer block={draggingBlock} selected={false} />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* ── Mobile: Bottom Sheet ──────────────────────────────────────────────── */}
      {isMobile && mobilePanel && (
        <>
          <div className="mobile-sheet-overlay" onClick={() => setMobilePanel(null)} />
          <div
            ref={sheetRef}
            className="mobile-bottom-sheet"
          >
            <div
              className="mobile-sheet-handle"
              onTouchStart={(e) => {
                sheetSwipeStartY.current = e.touches[0].clientY
                sheetSwipeDelta.current = 0
                if (sheetRef.current) sheetRef.current.style.transition = 'none'
              }}
              onTouchMove={(e) => {
                const delta = e.touches[0].clientY - sheetSwipeStartY.current
                if (delta < 0) return
                sheetSwipeDelta.current = delta
                if (sheetRef.current) sheetRef.current.style.transform = `translateY(${delta}px)`
              }}
              onTouchEnd={() => {
                if (sheetRef.current) sheetRef.current.style.transition = ''
                if (sheetSwipeDelta.current > 80) {
                  setMobilePanel(null)
                } else {
                  if (sheetRef.current) sheetRef.current.style.transform = ''
                }
                sheetSwipeDelta.current = 0
              }}
            />
            <div className="mobile-sheet-content">
              {(mobilePanel === 'blocks' || mobilePanel === 'layers') && (
                <div className="sidebar-tabs" style={{ margin: '0 8px 0' }}>
                  <button
                    className={`sidebar-tab ${mobilePanel === 'blocks' ? 'active' : ''}`}
                    onClick={() => setMobilePanel('blocks')}
                  >Блоки</button>
                  <button
                    className={`sidebar-tab ${mobilePanel === 'layers' ? 'active' : ''}`}
                    onClick={() => setMobilePanel('layers')}
                  >Слои</button>
                </div>
              )}
              {mobilePanel === 'blocks' && <BlockLibrary />}
              {mobilePanel === 'layers' && <LayersPanel />}
              {mobilePanel === 'pages' && <PagesPanel />}
              {mobilePanel === 'properties' && <PropertiesPanel />}
            </div>
          </div>
        </>
      )}

      {/* ── Mobile: More Menu ────────────────────────────────────────────────── */}
      {isMobile && mobileMoreOpen && (
        <>
          <div className="mobile-sheet-overlay" onClick={() => setMobileMoreOpen(false)} style={{ zIndex: 870 }} />
          <div className="mobile-bottom-sheet mobile-more-sheet" style={{ zIndex: 880 }}>
            <div className="mobile-sheet-handle" onClick={() => setMobileMoreOpen(false)} style={{ cursor: 'pointer' }} />
            <div style={{ padding: '4px 12px 8px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Инструменты
            </div>
            <div className="mobile-more-grid">
              {([
                { icon: '👁', label: 'Preview',    action: () => { window.open('/preview', '_blank'); setMobileMoreOpen(false) } },
                { icon: '✨', label: 'AI Сайт',    action: () => { setAiOpen(true); setMobileMoreOpen(false) } },
                { icon: '🎨', label: 'Тема',       action: () => { setThemeOpen(true); setMobileMoreOpen(false) } },
                { icon: '🖌', label: 'Стили',      action: () => { setStyleOpen(true); setMobileMoreOpen(false) } },
                { icon: '⚙', label: 'Настройки',  action: () => { setSettingsOpen(true); setMobileMoreOpen(false) } },
                { icon: '📊', label: 'Аналитика',  action: () => { setAnalyticsOpen(true); setMobileMoreOpen(false) } },
                { icon: '📝', label: 'CMS',        action: () => { setCmsOpen(true); setMobileMoreOpen(false) } },
                { icon: '📬', label: 'Заявки',     action: () => { setSubmissionsOpen(true); setMobileMoreOpen(false) } },
                { icon: '🖼', label: 'Медиа',      action: () => { setMediaOpen(true); setMobileMoreOpen(false) } },
                { icon: '🛒', label: 'Магазин',    action: () => { setShopOpen(true); setMobileMoreOpen(false) } },
                { icon: '🔍', label: 'SEO',        action: () => { setSeoOpen(true); setMobileMoreOpen(false) } },
                { icon: '🕐', label: 'История',    action: () => { setHistoryOpen(true); setMobileMoreOpen(false) } },
              ] as { icon: string; label: string; action: () => void }[]).map(item => (
                <button key={item.label} className="mobile-more-item" onClick={item.action}>
                  <span className="mobile-more-icon">{item.icon}</span>
                  <span className="mobile-more-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Mobile: Tab Bar ──────────────────────────────────────────────────── */}
      {isMobile && (
        <nav className="mobile-tab-bar" aria-label="Панель инструментов">
          {([
            { id: 'blocks',     label: 'Блоки',     icon: '⊞' },
            { id: 'layers',     label: 'Слои',      icon: '≡' },
            { id: 'pages',      label: 'Страницы',  icon: '📄' },
            { id: 'properties', label: 'Свойства',  icon: '⚙' },
            { id: 'ai',         label: 'AI Chat',   icon: '✨' },
          ] as const).map(tab => (
            <button
              key={tab.id}
              className={`mobile-tab-btn${mobilePanel === tab.id ? ' active' : ''}`}
              onClick={() => {
                setMobileMoreOpen(false)
                if (tab.id === 'ai') {
                  setAiChatOpen(v => !v)
                } else {
                  setMobilePanel(p => p === tab.id ? null : tab.id)
                }
              }}
            >
              <span style={{ fontSize: '18px', lineHeight: 1 }}>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  )
}

// ── Publish Modal ─────────────────────────────────────────────────────────────

import type { PageState } from './store/editor.store'

interface PublishModalProps {
  page: PageState
  siteId: string | null
  siteSlug: string
  totalPages: number
  totalBlocks: number
  published: boolean
  siteIsPublished: boolean
  copied: boolean
  error?: string | null
  onPublish: () => void
  onDownload: () => void
  onCopy: (url: string) => void
  onClose: () => void
}

function PublishModal({ page, siteId, siteSlug, totalPages, totalBlocks, published, siteIsPublished, copied, error, onPublish, onDownload, onCopy, onClose }: PublishModalProps) {
  const [scheduleMode, setScheduleMode] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')
  const [scheduling, setScheduling] = useState(false)
  const [scheduled, setScheduled] = useState(false)
  const siteUrl = `https://${siteSlug}.mybst.ru`

  const handleSchedule = async () => {
    if (!siteId || !scheduledAt) return
    setScheduling(true)
    try {
      await api.patch(`/sites/${siteId}/schedule`, { scheduledAt })
      setScheduled(true)
      setTimeout(() => setScheduled(false), 3000)
    } catch { /* noop */ } finally { setScheduling(false) }
  }

  const handleCancelSchedule = async () => {
    if (!siteId) return
    await api.patch(`/sites/${siteId}/schedule`, { scheduledAt: null }).catch(() => null)
    setScheduledAt('')
    setScheduleMode(false)
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '480px', maxWidth: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Публикация сайта</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{page.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Адрес сайта</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ flex: 1, padding: '9px 12px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {siteUrl}
            </div>
            <button
              onClick={() => onCopy(siteUrl)}
              style={{ padding: '9px 14px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, color: copied ? '#22c55e' : 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.2s' }}
            >
              {copied ? '✓ Скопировано' : 'Копировать'}
            </button>
          </div>
        </div>

        <div style={{ margin: '0 24px', padding: '16px', background: 'var(--bg-2)', borderRadius: '10px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
          {[
            { label: 'Страниц', value: String(totalPages) },
            { label: 'Блоков', value: String(totalBlocks) },
            { label: 'Статус', value: siteIsPublished ? 'Опубликован' : 'Черновик' },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            onClick={onPublish}
            style={{ padding: '12px', background: published ? '#22c55e' : '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            {published ? '✓ Опубликовано!' : '🚀 Опубликовать сейчас'}
          </button>

          {error && (
            <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#b91c1c' }}>
              ⚠ {error}
            </div>
          )}

          {/* Schedule toggle */}
          {!scheduleMode ? (
            <button
              onClick={() => setScheduleMode(true)}
              style={{ padding: '10px', background: 'var(--bg-2)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              🕐 Запланировать публикацию
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--bg-2)', borderRadius: '10px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Дата и время публикации</div>
              <input
                type="datetime-local"
                value={scheduledAt}
                min={new Date().toISOString().slice(0, 16)}
                onChange={e => setScheduledAt(e.target.value)}
                style={{ padding: '8px 10px', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)' }}
              />
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={handleSchedule}
                  disabled={scheduling || !scheduledAt}
                  style={{ flex: 1, padding: '8px', background: scheduled ? '#22c55e' : '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: !scheduledAt ? 0.5 : 1 }}
                >
                  {scheduled ? '✓ Запланировано' : scheduling ? 'Сохраняю...' : 'Запланировать'}
                </button>
                <button onClick={handleCancelSchedule} style={{ padding: '8px 12px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
              </div>
            </div>
          )}

          <button
            onClick={onDownload}
            style={{ padding: '12px', background: 'var(--bg-3)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            ⬇ Скачать HTML
          </button>

          {/* Embed code */}
          <details style={{ background: 'var(--bg-2)', borderRadius: '10px', border: '1px solid var(--border)' }}>
            <summary style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>{'</>'} Код для вставки (iframe)</summary>
            <div style={{ padding: '0 14px 12px' }}>
              <textarea
                readOnly
                value={`<iframe src="${siteUrl}" width="100%" height="600" frameborder="0" allowfullscreen loading="lazy" style="border-radius:8px"></iframe>`}
                style={{ width: '100%', height: '80px', padding: '8px', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '11px', fontFamily: 'monospace', resize: 'none', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                onClick={e => (e.target as HTMLTextAreaElement).select()}
              />
            </div>
          </details>

          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
            HTML-файл готов к размещению на любом хостинге
          </div>
        </div>
      </div>
    </div>
  )
}

// ── First-Publish Celebration Modal ──────────────────────────────────────────

function FirstPublishModal({ siteSlug, siteId, onClose }: { siteSlug: string; siteId: string | null; onClose: () => void }) {
  const siteUrl = `https://${siteSlug}.mybst.ru`
  const shareText = encodeURIComponent(`Только что создал сайт в BST — конструкторе с 376+ блоками и AI! Смотрите:`)
  const shareUrl = encodeURIComponent(siteUrl)
  const [gallerySubmitting, setGallerySubmitting] = useState(false)
  const [galleryDone, setGalleryDone] = useState(false)
  const [copied, setCopied] = useState(false)

  const shareLinks = [
    { icon: '✈', label: 'Telegram', bg: '#2aabee', url: `https://t.me/share/url?url=${shareUrl}&text=${shareText}` },
    { icon: '💬', label: 'WhatsApp', bg: '#25d366', url: `https://wa.me/?text=${shareText}%20${shareUrl}` },
    { icon: '📘', label: 'VK',       bg: '#0077ff', url: `https://vk.com/share.php?url=${shareUrl}&title=${shareText}` },
    { icon: '𝕏',  label: 'X',        bg: '#000',    url: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}` },
  ]

  async function submitToGallery() {
    if (!siteId || galleryDone) return
    setGallerySubmitting(true)
    try {
      await sitesApi.update(siteId, { publiclyListed: true } as never)
      setGalleryDone(true)
    } catch { /* noop */ } finally { setGallerySubmitting(false) }
  }

  function copyLink() {
    navigator.clipboard.writeText(siteUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: '20px', width: '480px', maxWidth: '100%', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.35)', position: 'relative' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #5b6af0, #7c3aed)', padding: '32px 32px 28px', textAlign: 'center', color: '#fff' }}>
          <div style={{ fontSize: '52px', marginBottom: '10px', lineHeight: 1 }}>🎉</div>
          <div style={{ fontSize: '22px', fontWeight: 800, marginBottom: '6px' }}>Сайт опубликован!</div>
          <div style={{ fontSize: '13px', opacity: 0.85 }}>Поделитесь им с миром</div>
        </div>
        <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '14px', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: '18px', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>

        <div style={{ padding: '24px 28px 28px' }}>
          {/* Site URL */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <div style={{ flex: 1, padding: '10px 14px', background: '#f3f4f6', borderRadius: '10px', fontSize: '13px', color: '#333', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {siteUrl}
            </div>
            <button onClick={copyLink} style={{ padding: '10px 16px', background: copied ? '#10b981' : '#111', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {copied ? '✓' : 'Копировать'}
            </button>
          </div>

          {/* Share buttons */}
          <div style={{ marginBottom: '6px', fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Поделиться в соцсетях
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            {shareLinks.map(link => (
              <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: '10px 6px', background: link.bg, color: '#fff', textDecoration: 'none', borderRadius: '10px', fontSize: '11px', fontWeight: 700 }}>
                <span style={{ fontSize: '18px', lineHeight: 1 }}>{link.icon}</span>
                {link.label}
              </a>
            ))}
          </div>

          {/* Gallery submit */}
          <div style={{ background: galleryDone ? '#f0fdf4' : '#f9fafb', border: `1px solid ${galleryDone ? '#86efac' : '#e5e7eb'}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px' }}>{galleryDone ? '✅' : '🌍'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '2px' }}>
                {galleryDone ? 'Добавлено в галерею BST!' : 'Добавить в галерею BST'}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {galleryDone ? 'Ваш сайт виден другим пользователям как вдохновение' : 'Ваш сайт появится в разделе «Вдохновение» для других пользователей'}
              </div>
            </div>
            {!galleryDone && (
              <button onClick={submitToGallery} disabled={gallerySubmitting}
                style={{ padding: '7px 14px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: gallerySubmitting ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                {gallerySubmitting ? '...' : 'Добавить'}
              </button>
            )}
          </div>

          <a href={siteUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', width: '100%', padding: '12px', background: '#5b6af0', color: '#fff', textDecoration: 'none', borderRadius: '10px', textAlign: 'center', fontSize: '14px', fontWeight: 700, boxSizing: 'border-box' }}>
            Открыть мой сайт →
          </a>
        </div>
      </div>
    </div>
  )
}
